import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { listResidentTimeline } from "@/api/residents";
import { formatRelativeTime } from "@/components/module_summary/formatRelativeTime";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

type ResidentActivityProps = {
  municipalityId: number;
  residentId: number;
};

export function ResidentActivity({ municipalityId, residentId }: ResidentActivityProps) {
  const { data: timeline = [], isLoading, error } = useQuery({
    queryKey: ["resident_timeline", municipalityId, residentId],
    queryFn: () => listResidentTimeline(municipalityId, residentId),
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading timeline…
      </div>
    );
  }

  if (error) {
    return <p className="py-4 text-sm text-destructive">Could not load timeline.</p>;
  }

  if (timeline.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No activity recorded yet.
      </p>
    );
  }

  return (
    <ul className="space-y-3" data-testid="resident-activity">
      {timeline.map((entry, index) => (
        <li
          key={`${entry.event_type}-${entry.occurred_at}-${index}`}
          className="flex items-start gap-3 rounded-lg border bg-background px-3 py-3"
        >
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-medium">{entry.label}</p>
              <Badge variant="outline" className="text-[10px] font-normal capitalize">
                {entry.source}
              </Badge>
            </div>
            {entry.body ? (
              <p className="mt-1 text-sm text-muted-foreground">{entry.body}</p>
            ) : null}
          </div>
          <span className="shrink-0 text-xs text-muted-foreground">
            {formatRelativeTime(entry.occurred_at)}
          </span>
        </li>
      ))}
    </ul>
  );
}
